import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { logger } from '../utils/logger';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { token, refreshCompanyData } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState('Verifying your payment...');
  const [pollAttempts, setPollAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async () => {
    const maxAttempts = 5;
    
    logger.debug(`Polling payment status (attempt ${pollAttempts + 1}/${maxAttempts}) for session:`, sessionId);
    
    if (pollAttempts >= maxAttempts) {
      setStatus('error');
      setMessage('Payment verification timed out. Please contact support or check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/stripe/checkout-status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setMessage('Payment successful! Your account has been activated.');
        
        // Refresh company data to update setup_fee_paid status
        await refreshCompanyData();
        
        // Redirect to home after 3 seconds
        setTimeout(() => navigate('/home'), 3000);
      } else if (response.data.status === 'expired') {
        setStatus('error');
        setMessage('Payment session expired. Please try again.');
      } else {
        // Continue polling
        setPollAttempts(prev => prev + 1);
        // The useEffect will handle the next poll attempt
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      console.error('Error response:', error.response?.data);
      setStatus('error');
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to verify payment. Please contact support.';
      setMessage(`Payment verification failed: ${errorMsg}`);
    }
  }, [sessionId, token, pollAttempts, navigate, refreshCompanyData, setPollAttempts, setStatus, setMessage]);

  useEffect(() => {
    if (sessionId && token && status === 'checking' && pollAttempts === 0) {
      logger.debug('Payment Success Page - Session ID:', sessionId);
      logger.debug('Payment Success Page - Token exists:', !!token);
      // Start polling by setting attempts to trigger the polling effect
      setPollAttempts(1);
    } else if (!sessionId || !token) {
      console.error('Missing session_id or token:', { sessionId, hasToken: !!token });
    }
  }, [sessionId, token, status, pollAttempts]);

  // Handle polling when pollAttempts changes
  useEffect(() => {
    if (pollAttempts > 0 && status === 'checking') {
      const timer = setTimeout(() => {
        pollPaymentStatus();
      }, pollAttempts === 1 ? 0 : 2000); // No delay for first attempt
      
      return () => clearTimeout(timer);
    }
  }, [pollAttempts, status, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-2xl">
        <CardContent className="p-12 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-blue-600 animate-spin mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Processing Payment</h2>
              <p className="text-gray-600 mb-4">{message}</p>
              <div className="flex justify-center space-x-1">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Successful!</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <p className="text-sm text-gray-500">Redirecting to your dashboard...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
                <AlertCircle className="w-12 h-12 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Issue</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/home')}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Go to Dashboard
                </Button>
                <Button
                  onClick={() => navigate('/settings')}
                  variant="outline"
                  className="w-full"
                >
                  Contact Support
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
